import { useState, useEffect } from 'react'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import { loadErrorMessage } from '../../utils/loadState'
import { Link } from 'react-router-dom'
import './AdminObservations.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'

const API = API_BASE_CONFIG
const getToken = () => localStorage.getItem('xlr8_token') || ''

async function apiFetch(path) {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export default function AdminObservations() {
  const [tab, setTab] = useState('catalog')
  const [catalog, setCatalog] = useState(null)
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList(list => {
    if (list.length > 0) setSelectedCustomer(prev => prev || list[0].customer_id)
  })
  const [activeChecks, setActiveChecks] = useState(null)
  const [allOverrides, setAllOverrides] = useState([])
  // A failed load is an error with a Retry -- never "No customer overrides configured yet"
  // or an endless "Loading checks…" (item 201).
  const [activeChecksError, setActiveChecksError] = useState(null)
  const [overridesError, setOverridesError] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedChecks, setExpandedChecks] = useState({})

  useEffect(() => { loadCatalog() }, [])
  useEffect(() => {
    if (selectedCustomer && tab === 'by-customer') { setActiveChecks(null); setExpandedChecks({}); loadActiveChecks(selectedCustomer) }
  }, [selectedCustomer, tab])
  useEffect(() => { if (tab === 'overrides') loadAllOverrides() }, [tab, customers])

  async function loadCatalog() {
    setLoading(true)
    try { setCatalog(await apiFetch('/api/observations/catalog/checks')) }
    catch (e) { setError(e.message) }
    setLoading(false)
  }
  async function loadActiveChecks(custId) {
    setActiveChecksError(null)
    try { setActiveChecks(await apiFetch(`/api/observations/${custId}/active-checks`)) }
    catch (e) {
      console.error('Failed to load active checks:', e)
      setActiveChecksError(loadErrorMessage(e))
    }
  }
  async function loadAllOverrides() {
    setOverridesError(null)
    const all = []
    const failed = []
    for (const c of customers) {
      try {
        const data = await apiFetch(`/api/observations/${c.customer_id}/overrides`)
        if (data.overrides?.length > 0) all.push(...data.overrides.map(o => ({ ...o, _customer_name: c.company_name || c.customer_id })))
      } catch {
        failed.push(c.company_name || c.customer_id)
      }
    }
    setAllOverrides(all)
    if (failed.length) setOverridesError(`Overrides could not be read for: ${failed.join(', ')}`)
  }
  // A failed delete is shown (item 201 leftovers): this never looked at the response, so a
  // refused or failed delete just re-listed the override as if nothing had been asked.
  async function deleteOverride(customerId, checkKey, overrideType) {
    if (!confirm(`Delete override "${checkKey}" (${overrideType}) for ${customerId}?`)) return
    setDeleteError(null)
    try {
      const res = await fetch(
        `${API}/api/observations/${encodeURIComponent(customerId)}/overrides/${encodeURIComponent(checkKey)}`
          + `?override_type=${encodeURIComponent(overrideType)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } })
      if (!res.ok) {
        let detail = ''
        try { detail = (await res.json())?.detail || '' } catch (parseErr) { detail = '' }
        throw new Error(detail ? `HTTP ${res.status}: ${detail}` : `HTTP ${res.status}`)
      }
      await loadAllOverrides()
    } catch (e) {
      console.error('Delete failed:', e)
      setDeleteError(`Could not delete override "${checkKey}" (${overrideType}) for ${customerId}: ${loadErrorMessage(e)}`)
    }
  }

  function toggleCheck(key) {
    setExpandedChecks(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const breadcrumbs = [{ label: 'Admin', to: '/admin' }, { label: 'Observations' }]
  const navActions = <Link to="/admin" className="nav-btn">← Admin</Link>
  const totalChecks = catalog?.total || 0
  const enabledChecks = catalog?.enabled || 0
  const categories = catalog?.by_category ? Object.keys(catalog.by_category) : []

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
      <div className="ao-page">
        <div className="ao-container">

          {/* Hero */}
          <div className="ao-hero">
            <div className="ao-hero-left">
              <div className="ao-hero-icon">⚡</div>
              <div>
                <div className="ao-hero-title">Observation Engine</div>
                <div className="ao-hero-meta">Check definitions, customer overrides, and per-customer activation</div>
              </div>
            </div>
            <div className="ao-hero-stats">
              <div className="ao-hero-stat"><div className="ao-hero-stat-value">{totalChecks}</div><div className="ao-hero-stat-label">Total Checks</div></div>
              <div className="ao-hero-stat"><div className="ao-hero-stat-value">{enabledChecks}</div><div className="ao-hero-stat-label">Enabled</div></div>
              <div className="ao-hero-stat"><div className="ao-hero-stat-value">{categories.length}</div><div className="ao-hero-stat-label">Categories</div></div>
            </div>
          </div>

          {/* Tabs */}
          <div className="ao-tabs">
            <button className={`ao-tab ${tab === 'catalog' ? 'active' : ''}`} onClick={() => setTab('catalog')}>
              Check Catalog <span className="ao-tab-badge">{totalChecks}</span>
            </button>
            <button className={`ao-tab ${tab === 'by-customer' ? 'active' : ''}`} onClick={() => setTab('by-customer')}>By Customer</button>
            <button className={`ao-tab ${tab === 'overrides' ? 'active' : ''}`} onClick={() => setTab('overrides')}>
              Overrides {allOverrides.length > 0 && <span className="ao-tab-badge">{allOverrides.length}</span>}
            </button>
          </div>

          {/* ═══ CATALOG TAB ═══ */}
          {tab === 'catalog' && catalog && Object.entries(catalog.by_category || {}).map(([category, checks]) => (
            <div className="ao-category-group" key={category}>
              <div className="ao-category-header">
                <div className="ao-category-name">{category}</div>
                <div className="ao-category-count">{checks.length} checks</div>
              </div>
              {checks.map(check => {
                let dp = {}; try { dp = JSON.parse(check.default_params || '{}') } catch {}
                return (
                  <div className={`ao-check ${!check.enabled ? 'disabled' : ''}`} key={check.check_key}>
                    <button className={`ao-check-toggle ${check.enabled ? 'on' : ''}`} />
                    <div className="ao-check-info">
                      <div className="ao-check-key">{check.check_key}</div>
                      <div className="ao-check-desc">{check.description || check.title_template}</div>
                      <div className="ao-check-tags">
                        <span className={`ao-check-tag severity-${check.severity_default}`}>{check.severity_default}</span>
                        <span className={`ao-check-tag scope-${check.scope === 'us_only' ? 'us' : check.scope === 'international' ? 'intl' : 'global'}`}>
                          {check.scope === 'us_only' ? 'US Only' : check.scope === 'international' ? 'International' : 'Global'}
                        </span>
                        <span className={`ao-check-tag type-${check.check_type === 'cross_table' ? 'cross' : 'per'}`}>
                          {check.check_type === 'cross_table' ? 'Cross-table' : 'Per-table'}
                        </span>
                        {check.vendor_hint && <span className="ao-check-tag" style={{ background: '#e0e7ff', color: '#4338ca' }}>{check.vendor_hint}</span>}
                      </div>
                      {Object.keys(dp).length > 0 && <div className="ao-check-params">defaults: {JSON.stringify(dp, null, 2)}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}

          {/* ═══ BY CUSTOMER TAB ═══ */}
          {tab === 'by-customer' && (
            <>
              {customersError && <LoadError what="customers" error={customersError} onRetry={reloadCustomers} />}
              <div className="ao-customer-select">
                <label>Customer:</label>
                <select value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)}>
                  {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.company_name || c.customer_id}</option>)}
                </select>
                {activeChecks && (
                  <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 8 }}>
                    {activeChecks.observations_error
                      ? `couldn't load findings: ${activeChecks.observations_error}`
                      : `${activeChecks.total_observations || activeChecks.checks?.reduce((s, c) => s + (c.observation_count || 0), 0)} total observations`}
                  </span>
                )}
              </div>

              {activeChecks && (
                <>
                  <div className="ao-customer-summary">
                    <div className="ao-customer-summary-item">
                      Categories: <strong>{activeChecks.active_categories?.filter(c => c !== '_all_').join(', ') || 'All (unconfigured)'}</strong>
                    </div>
                    <div className="ao-customer-summary-item">Scope: <strong>{activeChecks.customer_scope}</strong></div>
                    <div className="ao-customer-summary-item">Running: <strong style={{ color: 'var(--green-dark)' }}>{activeChecks.running}</strong> / {activeChecks.total_checks}</div>
                    <div className="ao-customer-summary-item">Skipped: <strong style={{ color: activeChecks.skipped > 0 ? 'var(--red)' : 'var(--gray-400)' }}>{activeChecks.skipped}</strong></div>
                    <div className="ao-customer-summary-item">Overrides: <strong>{activeChecks.overrides_active}</strong></div>
                  </div>

                  <div className="ao-check-list">
                    {activeChecks.checks?.map(check => {
                      const isExpanded = !!expandedChecks[check.check_key]
                      const obsCount = check.observation_count || 0
                      const hasObs = obsCount > 0
                      const tables = check.tables_touched || []
                      const observations = check.observations || []

                      return (
                        <div key={check.check_key} className={`ao-check-row-group ${isExpanded ? 'expanded' : ''}`}>
                          <div
                            className={`ao-check-row ${hasObs ? 'has-obs' : ''} ${!check.will_run ? 'skipped' : ''}`}
                            onClick={() => hasObs && toggleCheck(check.check_key)}
                          >
                            <div className={`ao-active-dot ${check.will_run ? 'running' : 'skipped'}`} />
                            <div className="ao-check-row-name">
                              {check.check_key}
                            </div>
                            {hasObs && (
                              <span className="ao-obs-count-pill">{obsCount} finding{obsCount > 1 ? 's' : ''}</span>
                            )}
                            <div className="ao-check-row-cat">{check.category}</div>
                            {check.skip_reason && <div className="ao-check-row-reason">{check.skip_reason}</div>}
                            {check.overrides?.length > 0 && (
                              <span className="ao-override-count-badge">{check.overrides.length} override{check.overrides.length > 1 ? 's' : ''}</span>
                            )}
                            {hasObs && (
                              <span className="ao-check-row-chevron">{isExpanded ? '▾' : '▸'}</span>
                            )}
                          </div>

                          {isExpanded && (
                            <div className="ao-check-detail">
                              {/* Tables */}
                              {tables.length > 0 && (
                                <div className="ao-detail-section">
                                  <div className="ao-detail-label">Tables Queried ({tables.length})</div>
                                  <div className="ao-detail-tables">
                                    {tables.map(t => <span key={t} className="ao-detail-table-tag">{t.replace(/^cust_[^_]+_/, '')}</span>)}
                                  </div>
                                </div>
                              )}

                              {/* Each observation with SQL */}
                              {observations.map((o, i) => (
                                <div key={o.id || i} className="ao-detail-obs">
                                  <div className="ao-detail-obs-header">
                                    <span className={`ao-detail-severity ${o.severity}`}>
                                      {o.severity === 'critical' ? '!' : o.severity === 'warning' ? '⚠' : 'ⓘ'}
                                    </span>
                                    <span className="ao-detail-obs-title">{o.title}</span>
                                    {o.table_name && (
                                      <span className="ao-detail-obs-table">
                                        {o.table_name.replace(/^cust_[^_]+_/, '')}{o.column ? ` → ${o.column}` : ''}
                                      </span>
                                    )}
                                  </div>
                                  {o.sql && (
                                    <div className="ao-detail-sql-block">
                                      <div className="ao-detail-sql-label">SQL</div>
                                      <pre className="ao-detail-sql-code">{o.sql}</pre>
                                    </div>
                                  )}
                                  {o.drill_sql && o.drill_sql.trim() && (
                                    <div className="ao-detail-sql-block drill">
                                      <div className="ao-detail-sql-label">Drill SQL</div>
                                      <pre className="ao-detail-sql-code">{o.drill_sql}</pre>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
              {activeChecksError && <LoadError what={`checks for ${selectedCustomer}`} error={activeChecksError} onRetry={() => loadActiveChecks(selectedCustomer)} />}
              {!activeChecks && !activeChecksError && selectedCustomer && <div className="ao-empty">Loading checks for {selectedCustomer}...</div>}
            </>
          )}

          {/* ═══ OVERRIDES TAB ═══ */}
          {tab === 'overrides' && (
            <>
              {customersError && <LoadError what="customers" error={customersError} onRetry={reloadCustomers} />}
              {overridesError && <LoadError what="overrides" error={overridesError} onRetry={loadAllOverrides} />}
              {deleteError && <div className="ao-empty" role="alert" style={{ color: 'var(--red)' }}>{deleteError}</div>}
              {!customersError && !overridesError && allOverrides.length === 0 && (
                <div className="ao-empty">
                  <div className="ao-empty-icon">🎯</div>
                  <div>No customer overrides configured yet.</div>
                  <div style={{ marginTop: 4 }}>Overrides are created from the customer profile when marking observations.</div>
                </div>
              )}
              {allOverrides.map((o, i) => (
                <div className="ao-override-item" key={i}>
                  <div className="ao-override-customer">{o._customer_name || o.customer_id}</div>
                  <div className="ao-override-info">
                    <div className="ao-override-check">
                      {o.check_key}<span className={`ao-override-type ${o.override_type}`}>{o.override_type}</span>
                    </div>
                    {o.reason && <div className="ao-override-reason">{o.reason}</div>}
                    <div className="ao-override-meta">
                      {o.params && Object.keys(o.params).length > 0 && <span style={{ fontFamily: 'var(--font-mono)' }}>{JSON.stringify(o.params)}</span>}
                      {o.created_by && <span> · by {o.created_by}</span>}
                    </div>
                  </div>
                  <button className="ao-override-delete" onClick={() => deleteOverride(o.customer_id, o.check_key, o.override_type)}>Delete</button>
                </div>
              ))}
            </>
          )}

          {loading && !catalog && <div className="ao-empty">Loading...</div>}
          {error && <div className="ao-empty" style={{ color: 'var(--red)' }}>Error: {error}</div>}
        </div>
      </div>
    </>
  )
}
